/**
 * Streaming de precios del Nectum MT5 Gateway local (wss://localhost:8444/gateway/ws/prices) con la
 * misma interfaz que la StreamingConnection del SDK de MetaAPI que usa MetaApiStreamingService:
 * connect, waitSynchronized, subscribe/unsubscribeToMarketData, terminalState.price,
 * subscribedSymbols, close y los eventos onSymbolPriceUpdated/onConnected/onDisconnected.
 *
 * Protocolo: cliente → {type: 'subscribe' | 'unsubscribe', symbols}; servidor → {type: 'prices',
 * prices: MetatraderSymbolPrice[]} y {type: 'status', terminalConnected}.
 * Si el socket se cae, se reconecta solo con backoff (como hace el SDK) y vuelve a suscribir.
 */
export class Mt5GatewayStreamingConnection {
  private static readonly INSTANCE = '0';
  private static readonly FIRST_QUOTE_TIMEOUT_MS = 5_000;
  private static readonly RECONNECT_MIN_MS = 1_000;
  private static readonly RECONNECT_MAX_MS = 30_000;

  private ws: WebSocket | null = null;
  private listeners: any[] = [];
  private symbols = new Set<string>();
  private prices = new Map<string, any>();
  private waiters = new Map<string, Array<() => void>>();
  private closed = false;
  private online = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 0;

  readonly terminalState = {
    price: (symbol: string) => this.prices.get(symbol)
  };

  constructor(private readonly url: string) {}

  get subscribedSymbols(): string[] {
    return Array.from(this.symbols);
  }

  addSynchronizationListener(listener: any): void {
    this.listeners.push(listener);
  }

  removeSynchronizationListener(listener: any): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  /** Resuelve al abrir el socket; rechaza si el gateway no está escuchando (no instalado o parado). */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => this.open(resolve, reject));
  }

  /** No hay sincronización de estado como en MetaAPI: con el socket abierto ya llegan precios. */
  waitSynchronized(_opts?: any): Promise<void> {
    return this.online ? Promise.resolve() : Promise.reject(new Error('Gateway MT5 sin conexión'));
  }

  /** Resuelve con la primera cotización del símbolo (o a los pocos segundos, como el SDK). */
  subscribeToMarketData(symbol: string, _subscriptions?: any[]): Promise<void> {
    this.symbols.add(symbol);
    this.send({ type: 'subscribe', symbols: [symbol] });
    if (this.prices.has(symbol)) return Promise.resolve();
    return new Promise(resolve => {
      const done = () => { clearTimeout(timer); resolve(); };
      const timer = setTimeout(() => {
        this.waiters.set(symbol, (this.waiters.get(symbol) ?? []).filter(w => w !== done));
        resolve();
      }, Mt5GatewayStreamingConnection.FIRST_QUOTE_TIMEOUT_MS);
      this.waiters.set(symbol, [...(this.waiters.get(symbol) ?? []), done]);
    });
  }

  unsubscribeFromMarketData(symbol: string, _subscriptions?: any[]): Promise<void> {
    this.symbols.delete(symbol);
    this.send({ type: 'unsubscribe', symbols: [symbol] });
    return Promise.resolve();
  }

  close(): Promise<void> {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const ws = this.ws;
    this.ws = null;
    try { ws?.close(); } catch {}
    return Promise.resolve();
  }

  // ---------------------------------------------------------------------------

  private open(onOpen?: () => void, onFail?: (err: any) => void): void {
    let opened = false;
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch (err) {
      onFail?.(err);
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      opened = true;
      this.online = true;
      this.reconnectDelay = 0;
      if (this.symbols.size > 0) this.send({ type: 'subscribe', symbols: this.subscribedSymbols });
      this.emit('onConnected');
      onOpen?.();
    };
    ws.onmessage = event => this.onMessage(event.data);
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (!opened) {
        onFail?.(new Error('No se pudo conectar con el Gateway MT5 local'));
        if (!onFail) this.scheduleReconnect();
        return;
      }
      this.online = false;
      this.emit('onDisconnected');
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.closed || this.reconnectTimer) return;
    this.reconnectDelay = this.reconnectDelay
      ? Math.min(this.reconnectDelay * 2, Mt5GatewayStreamingConnection.RECONNECT_MAX_MS)
      : Mt5GatewayStreamingConnection.RECONNECT_MIN_MS;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.closed) this.open();
    }, this.reconnectDelay);
  }

  private onMessage(data: any): void {
    let message: any;
    try {
      message = JSON.parse(String(data));
    } catch {
      return;
    }
    if (message?.type === 'prices') {
      for (const price of message.prices ?? []) {
        if (!price?.symbol) continue;
        this.prices.set(price.symbol, price);
        this.emit('onSymbolPriceUpdated', price);
        const waiting = this.waiters.get(price.symbol);
        if (waiting) {
          this.waiters.delete(price.symbol);
          waiting.forEach(done => done());
        }
      }
    } else if (message?.type === 'status') {
      // Terminal MT5 cerrado o sin conexión con el bróker: para el servicio es una caída del streaming
      if (message.terminalConnected === false && this.online) {
        this.online = false;
        this.emit('onDisconnected');
      } else if (message.terminalConnected === true && !this.online && this.ws) {
        this.online = true;
        this.emit('onConnected');
      }
    }
  }

  private send(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private emit(event: 'onConnected' | 'onDisconnected' | 'onSymbolPriceUpdated', price?: any): void {
    for (const listener of this.listeners) {
      try {
        const result = event === 'onSymbolPriceUpdated'
          ? listener[event]?.(Mt5GatewayStreamingConnection.INSTANCE, price)
          : listener[event]?.(Mt5GatewayStreamingConnection.INSTANCE);
        result?.catch?.(() => {});
      } catch {}
    }
  }
}
